// frontend/web/src/pages/products/MyProductsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActions,
  CardMedia, Chip, CircularProgress, Alert, IconButton, Menu,
  MenuItem, Divider, TextField, FormControl, InputLabel, Select,
  Pagination, Tooltip, Paper, useTheme,
  Checkbox, Toolbar, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  PauseCircle as PauseCircleIcon,
  Visibility as VisibilityIcon,
  Publish as PublishIcon,
  PauseCircle as PauseIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  Inventory2 as InventoryIcon,
  Close as CloseIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useRestrictionBanner } from '../../hooks/useRestrictions';
import { useActivitySuspension } from '../../hooks/useActivitySuspension';
import productService from '../../services/productService';
import ListingProductSidebar from '../../components/common/ListingProductSidebar';
import CreateChoiceModal from '../../components/common/CreateChoiceModal';
import { getProductConditionTranslationKey } from '../../constants/productConditions';

const UPLOADS_BASE_URL =
  process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getThumbUrl = (images = []) => {
  const first = images[0];
  if (!first) return null;
  if (first.startsWith('http')) return first;
  return `${UPLOADS_BASE_URL}${first.startsWith('/') ? first : '/' + first}`;
};

const getStatusConfig = (t) => ({
  draft:    { label: t('status.draft'),    color: 'default' },
  pending:  { label: t('status.pending'),  color: 'warning' },
  active:   { label: t('status.active'),   color: 'success' },
  paused:   { label: t('status.paused'),   color: 'warning' },
  rejected: { label: t('status.rejected'), color: 'error' },
  archived: { label: t('status.archived'), color: 'default' },
  disabled: { label: t('status.disabled', 'Désactivé (ban)'), color: 'error' }
});

// ─── Menu actions carte produit ───────────────────────────────────────────────

function ProductCardMenu({ product, onEdit, onView, onToggleStatus, onDelete, t, userIsBanned }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  // Si l'utilisateur est banni, seuls les accès en lecture (Voir) sont autorisés,
  // on ne se base pas sur le statut du produit (`disabled`) ici.
  if (userIsBanned) {
    return (
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(); }}>
        <VisibilityIcon />
      </IconButton>
    );
  }

  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { onView(); setAnchorEl(null); }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> {t('actions.view')}
        </MenuItem>
        <MenuItem onClick={() => { onEdit(); setAnchorEl(null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> {t('actions.edit')}
        </MenuItem>
        {product.status === 'paused' && (
          <MenuItem onClick={() => { onToggleStatus('active'); setAnchorEl(null); }}>
            <PublishIcon fontSize="small" sx={{ mr: 1 }} /> {t('actions.activate')}
          </MenuItem>
        )}
        {product.status === 'active' && (
          <MenuItem onClick={() => { onToggleStatus('paused'); setAnchorEl(null); }}>
            <PauseIcon fontSize="small" sx={{ mr: 1 }} /> {t('actions.pause')}
          </MenuItem>
        )}
        {product.status === 'pending' && (
          <MenuItem disabled>
            <CircularProgress size={14} sx={{ mr: 1 }} /> {t('actions.awaiting')}
          </MenuItem>
        )}
        {product.status === 'rejected' && (
          <MenuItem onClick={() => { onToggleStatus('pending'); setAnchorEl(null); }}>
            <PublishIcon fontSize="small" sx={{ mr: 1 }} /> {t('actions.resubmit')}
          </MenuItem>
        )}
        <Divider />
        <Tooltip title={product.has_reservations ? t('errors.cannotDeleteWithReservations') : ''}>
          <span>
            <MenuItem
              onClick={() => { if (!product.has_reservations) { onDelete(); setAnchorEl(null); } }}
              disabled={product.has_reservations}
              sx={{ color: product.has_reservations ? 'text.disabled' : 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> {t('actions.delete')}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function MyProductsPage() {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isBanned, banType } = useRestrictionBanner();
  // Compute a single flag for the user's ban state (permanent or temporary)
  const userIsBanned = !!(isBanned && (banType === 'permanent' || banType === 'temporary'));
  const { isSuspended: userIsActivitySuspended } = useActivitySuspension();
  const { showNotification } = useNotifications();
  const theme = useTheme();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('DESC');
  const debounceRef = useRef(null);
  const genRef = useRef(0);

  // ── Sélection / bulk ──────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialog suppression individuelle
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Dialog pause individuelle
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [productToToggle, setProductToToggle] = useState(null);
  const [pendingToggleStatus, setPendingToggleStatus] = useState(null);

  // Dialogs bulk
  const [bulkPauseDialogOpen, setBulkPauseDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkMode, setBulkMode] = useState(null); // null | 'pause' | 'delete'
  
  // ── État pour le modal de choix création ──────────────────────────────────
  const [createChoiceModalOpen, setCreateChoiceModalOpen] = useState(false);

  const LIMIT = 12;

  // Recalcule totalPages quand totalCount change suite à une mutation locale
  // et revient à la dernière page valide si la page courante est hors limites.
  const updateCountAfterDelete = useCallback((delta) => {
    setTotalCount(prev => {
      const newCount = Math.max(0, prev - delta);
      const newTotalPages = Math.max(1, Math.ceil(newCount / LIMIT));
      setTotalPages(newTotalPages);
      setPage(p => (p > newTotalPages ? newTotalPages : p));
      return newCount;
    });
  }, [LIMIT]);

  const fetchProducts = useCallback(async (currentPage, status, search, currentSortBy, currentSortDir) => {
    const gen = ++genRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await productService.getMyProducts({
        page: currentPage,
        limit: LIMIT,
        status: status || undefined,
        search: search?.trim() || undefined,
        sortBy: currentSortBy,
        sortDir: currentSortDir
      });
      if (gen !== genRef.current) return; // Réponse périmée
      setProducts(res.products || []);
      setTotalPages(res.totalPages || 1);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      if (gen !== genRef.current) return;
      setError(t('errors.fetchFailed'));
    } finally {
      if (gen !== genRef.current) return;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(page, statusFilter, searchFilter, sortBy, sortDir);
  }, [page, statusFilter, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value) => {
    setSearchFilter(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchProducts(1, statusFilter, value, sortBy, sortDir);
    }, 400);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return ''; }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const diffDays = Math.ceil(Math.abs(new Date() - date) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return t('common:listing.date.today');
      if (diffDays === 1) return t('common:listing.date.yesterday');
      if (diffDays < 7) return t('common:listing.date.daysAgo', { count: diffDays });
      if (diffDays < 30) return t('common:listing.date.weeksAgo', { count: Math.ceil(diffDays / 7) });
      if (diffDays < 365) return t('common:listing.date.monthsAgo', { count: Math.ceil(diffDays / 30) });
      return t('common:listing.date.yearsAgo', { count: Math.ceil(diffDays / 365) });
    } catch { return ''; }
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleToggleStatus = (product, newStatus) => {
    // Pas de dialog pour réactivation / resoumettre, uniquement pour la mise en pause
    if (newStatus === 'paused') {
      setProductToToggle(product);
      setPendingToggleStatus(newStatus);
      setPauseDialogOpen(true);
    } else {
      doToggleStatus(product, newStatus);
    }
  };

  const doToggleStatus = async (product, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      await productService.update(product.id, formData);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, status: newStatus } : p));
      showNotification(t('form.successUpdate'), 'success');
    } catch (err) {
      showNotification(t('errors.fetchFailed'), 'error');
    }
  };

  const handleConfirmPause = async () => {
    if (!productToToggle) return;
    setSubmitting(true);
    await doToggleStatus(productToToggle, pendingToggleStatus);
    setSubmitting(false);
    setPauseDialogOpen(false);
    setProductToToggle(null);
    setPendingToggleStatus(null);
  };

  const handleDelete = (productId) => {
    setProductToDelete(productId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    setSubmitting(true);
    try {
      await productService.delete(productToDelete);
      setProducts(prev => prev.filter(p => p.id !== productToDelete));
      updateCountAfterDelete(1);
      setSelectedIds(prev => { const next = new Set(prev); next.delete(productToDelete); return next; });
      showNotification(t('deleteConfirm.success'), 'success');
    } catch (err) {
      if (err?.response?.status === 409 || err?.response?.data?.code === 'PRODUCT_HAS_RESERVATIONS') {
        showNotification(t('errors.cannotDeleteWithReservations'), 'error');
        // Mettre à jour le flag localement pour désactiver le bouton
        setProducts(prev => prev.map(p => p.id === productToDelete ? { ...p, has_reservations: true } : p));
      } else {
        const msg = err?.response?.data?.message || t('errors.deleteFailed');
        showNotification(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error');
      }
    } finally {
      setSubmitting(false);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  // ── Sélection ─────────────────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectableProducts = bulkMode === 'pause'
    ? products
    : bulkMode === 'delete'
      ? products.filter(p => !p.has_reservations)
      : [];

  const toggleSelectAll = () => {
    const allSelectableSelected = selectableProducts.length > 0 && selectableProducts.every(p => selectedIds.has(p.id));
    if (allSelectableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableProducts.map(p => p.id)));
    }
  };

  // ── Bulk actions ──────────────────────────────────────────────────────────

  const handleBulkPause = async () => {
    setBulkSubmitting(true);
    try {
      const ids = Array.from(selectedIds).filter(id => {
        const p = products.find(pr => pr.id === id);
        return p && p.status === 'active';
      });
      await productService.bulkPause(ids);
      setProducts(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: 'paused' } : p));
      setSelectedIds(new Set());
      showNotification(t('myProducts.bulk.pauseSuccess', { count: ids.length }), 'success');
    } catch (err) {
      showNotification(t('errors.fetchFailed'), 'error');
    } finally {
      setBulkSubmitting(false);
      setBulkPauseDialogOpen(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkSubmitting(true);
    try {
      const ids = Array.from(selectedIds);
      const result = await productService.bulkDelete(ids);
      const deleted = result.deleted ?? ids.length;
      const skipped = result.skipped ?? 0;
      setProducts(prev => prev.filter(p => !ids.includes(p.id) || p.has_reservations));
      updateCountAfterDelete(deleted);
      setSelectedIds(new Set());
      if (skipped > 0) {
        showNotification(t('myProducts.bulk.deleteSuccessWithSkipped', { count: deleted, skipped }), 'warning');
      } else {
        showNotification(t('myProducts.bulk.deleteSuccess', { count: deleted }), 'success');
      }
    } catch (err) {
      showNotification(t('errors.deleteFailed'), 'error');
    } finally {
      setBulkSubmitting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  return (
    <Box sx={{ width: '90%', maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      {/* ── Gradient Header ─── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('myProducts.title')}{" "}
                {totalCount > 0 && `(${totalCount})`}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {t('common:navigation.myProductsPageDescription')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateChoiceModalOpen(true)}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            fontWeight: 'medium',
            px: 3,
            py: 1.5,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('myProducts.createNew')}
        </Button>
      </Box>

      {/* ── Barre d'actions groupées (n'apparaît que si ≥1 sélectionné) ─── */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={3}
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: 'primary.dark',
            color: 'white',
            overflow: 'hidden'
          }}
        >
          <Toolbar sx={{ gap: 2, minHeight: '56px !important' }}>
            <Checkbox
              checked={selectableProducts.length > 0 && selectableProducts.every(p => selectedIds.has(p.id))}
              indeterminate={selectableProducts.some(p => selectedIds.has(p.id)) && !selectableProducts.every(p => selectedIds.has(p.id))}
              onChange={toggleSelectAll}
              sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
            />
            <Typography sx={{ flexGrow: 1, fontWeight: 'bold', color: 'white' }}>
              {t('myProducts.bulk.selected', { count: selectedIds.size })}
            </Typography>
            {bulkMode === 'pause' && (
              <Tooltip title={t('myProducts.bulk.pause')}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<PauseCircleIcon />}
                    onClick={() => setBulkPauseDialogOpen(true)}
                    disabled={!products.some(p => selectedIds.has(p.id) && p.status === 'active')}
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'warning.dark' },
                      textTransform: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('myProducts.bulk.pause')}
                  </Button>
                </span>
              </Tooltip>
            )}
            {bulkMode === 'delete' && (
              <Tooltip title={t('myProducts.bulk.delete')}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    sx={{
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                      textTransform: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('myProducts.bulk.delete')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title={t('myProducts.bulk.deselectAll')}>
              <IconButton onClick={() => { setSelectedIds(new Set()); setBulkMode(null); }} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Paper>
      )}

      {/* ── Filtres ─── */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {bulkMode === null ? (
              <>
                <Tooltip title={t('myProducts.bulk.pause')}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<PauseCircleIcon />}
                    onClick={() => { setBulkMode('pause'); setSelectedIds(new Set()); }}
                    disabled={products.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('myProducts.bulk.pause')}
                  </Button>
                </Tooltip>
                <Tooltip title={t('myProducts.bulk.delete')}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => { setBulkMode('delete'); setSelectedIds(new Set()); }}
                    disabled={products.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('myProducts.bulk.delete')}
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title={selectableProducts.length > 0 && selectableProducts.every(p => selectedIds.has(p.id)) ? t('myProducts.bulk.deselectAll') : t('myProducts.bulk.selectAll')}>
                  <span>
                    <Checkbox
                      checked={selectableProducts.length > 0 && selectableProducts.every(p => selectedIds.has(p.id))}
                      indeterminate={selectableProducts.some(p => selectedIds.has(p.id)) && !selectableProducts.every(p => selectedIds.has(p.id))}
                      onChange={toggleSelectAll}
                      disabled={selectableProducts.length === 0}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={t('myProducts.bulk.cancel', 'Annuler')}>
                  <IconButton size="small" onClick={() => { setBulkMode(null); setSelectedIds(new Set()); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              placeholder={t('form.title')}
              size="small"
              fullWidth
              value={searchFilter}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('myProducts.filterByStatus', 'Statut')}</InputLabel>
              <Select value={statusFilter} onChange={handleStatusChange} label={t('myProducts.filterByStatus', 'Statut')}>
                <MenuItem value="">{t('status.all', 'Tous')}</MenuItem>
                <MenuItem value="pending">{t('status.pending')}</MenuItem>
                <MenuItem value="active">{t('status.active')}</MenuItem>
                <MenuItem value="paused">{t('status.paused')}</MenuItem>
                <MenuItem value="rejected">{t('status.rejected')}</MenuItem>
                <MenuItem value="archived">{t('status.archived')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('myProducts.sortBy', 'Trier par')}</InputLabel>
              <Select
                value={`${sortBy}|${sortDir}`}
                onChange={(e) => {
                  const sep = e.target.value.lastIndexOf('|');
                  const field = e.target.value.slice(0, sep);
                  const dir = e.target.value.slice(sep + 1);
                  setSortBy(field);
                  setSortDir(dir);
                  setPage(1);
                }}
                label={t('myProducts.sortBy', 'Trier par')}
              >
                <MenuItem value="createdAt|DESC">{t('myProducts.sort.createdDesc', 'Plus récent d\'abord')}</MenuItem>
                <MenuItem value="createdAt|ASC">{t('myProducts.sort.createdAsc', 'Plus ancien d\'abord')}</MenuItem>
                <MenuItem value="price_cents|DESC">{t('myProducts.sort.priceDesc', 'Prix décroissant')}</MenuItem>
                <MenuItem value="price_cents|ASC">{t('myProducts.sort.priceAsc', 'Prix croissant')}</MenuItem>
                <MenuItem value="title|ASC">{t('myProducts.sort.titleAsc', 'Titre A → Z')}</MenuItem>
                <MenuItem value="title|DESC">{t('myProducts.sort.titleDesc', 'Titre Z → A')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Contenu */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={
          <Button size="small" onClick={() => fetchProducts(page, statusFilter, searchFilter, sortBy, sortDir)}>
            {t('common:retry', 'Réessayer')}
          </Button>
        }>{t('products.errors.fetchFailed')}</Alert>
      )}

      {!loading && !error && products.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            borderRadius: 3,
            background: 'linear-gradient(145deg, #f8f9ff 0%, #f0f2ff 100%)',
            border: '2px dashed #e0e7ff',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6200ee 0%, #03dac4 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 10px 30px rgba(98, 0, 238, 0.3)'
            }}
          >
            <InventoryIcon sx={{ fontSize: 60, color: 'white' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {t('myProducts.noProducts')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
            {t('myProducts.startCreating')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setCreateChoiceModalOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(98,0,238,0.4)' },
              transition: 'all 0.3s ease'
            }}
          >
            {t('myProducts.createFirst')}
          </Button>
        </Box>
      )}

      {!loading && !error && products.length > 0 && (
        <>
          <Grid container spacing={3}>
            {products.map((product) => {
              const thumb = getThumbUrl(product.images);
              const statusConf = getStatusConfig(t)[product.status] || getStatusConfig(t).draft;
              const priceFormatted = product.price_cents
                ? `${(product.price_cents / 100).toFixed(2)} ${product.currency}`
                : '—';
              const conditionLabel = product.condition
                ? t(getProductConditionTranslationKey(product.condition))
                : t('conditions.unknown');

              return (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                    onClick={() => navigate(`/product/${product.id}`)}
                  >

                    {/* Image avec overlay */}
                    <Box sx={{ position: 'relative', height: 180 }}>
                      {thumb ? (
                        <CardMedia
                          component="img"
                          height={180}
                          image={thumb}
                          alt={product.title}
                          sx={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 180,
                            background: 'linear-gradient(135deg, #f0f2ff 0%, #e8eaff 100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                          }}
                        >
                          <InventoryIcon sx={{ fontSize: 48, color: 'primary.light', opacity: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {t('detail.noImage')}
                          </Typography>
                        </Box>
                      )}
                      {/* Status chip overlay */}
                      <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                        <Chip
                          size="small"
                          label={statusConf.label}
                          color={statusConf.color}
                          sx={{ fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                        />
                      </Box>
                      {/* Checkbox overlay (top-right) — visible uniquement si un mode bulk est actif */}
                      {bulkMode !== null && (
                        <Box
                          sx={{ position: 'absolute', top: 4, right: 4 }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {bulkMode === 'delete' && product.has_reservations ? (
                            <Tooltip title={t('errors.cannotDeleteWithReservations')}>
                              <span>
                                <Checkbox
                                  checked={false}
                                  disabled
                                  size="small"
                                  sx={{
                                    bgcolor: 'rgba(255,255,255,0.85)',
                                    borderRadius: 1,
                                    p: 0.5
                                  }}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <Checkbox
                              checked={selectedIds.has(product.id)}
                              onChange={() => toggleSelect(product.id)}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.85)',
                                borderRadius: 1,
                                p: 0.5
                              }}
                            />
                          )}
                        </Box>
                      )}
                      {/* Price overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.65)',
                          color: 'white',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          backdropFilter: 'blur(4px)'
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          {priceFormatted}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Contenu */}
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      {/* Titre */}
                      <Typography variant="subtitle1" fontWeight={600} noWrap gutterBottom>
                        {product.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${t('conditions.label')}: ${conditionLabel}`}
                        variant="outlined"
                        sx={{ mb: 1, fontWeight: 600 }}
                      />

                      {/* Description */}
                      {product.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {product.description}
                        </Typography>
                      )}

                      {/* Motif de rejet */}
                      {product.status === 'rejected' && product.rejection_reason && (
                        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            <strong>{t('form.rejectionReason')} :</strong>{' '}
                            {product.rejection_reason}
                          </Typography>
                        </Alert>
                      )}

                      {/* Verrou réservations — suppression impossible */}
                      {product.has_reservations && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            {t('errors.hasReservationsNotice')}
                          </Typography>
                        </Alert>
                      )}

                      {/* En attente */}
                      {product.status === 'pending' && (
                        <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            {t('actions.awaiting')}
                          </Typography>
                        </Alert>
                      )}

                      {/* Désactivé suite à un ban */}
                      {userIsBanned && (
                        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            {t('status.disabledBanNotice', 'Ce produit a été désactivé suite à la suspension de votre compte.')}
                          </Typography>
                        </Alert>
                      )}

                      {/* Suspendu suite à suspension volontaire d'activité */}
                      {userIsActivitySuspended && product.status === 'disabled' && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            {t('common:activitySuspension.cardNotice')}
                          </Typography>
                        </Alert>
                      )}

                      {/* Chips infos */}
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {product.pickup_location && (
                          <Tooltip title={product.pickup_location.address || t('details.location')}>
                            <Chip
                              size="small"
                              icon={<LocationOnIcon />}
                              label={t('details.location')}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {product.availabilitySlots?.length > 0 ? (
                          <Chip
                            size="small"
                            icon={<ScheduleIcon />}
                            label={`${product.availabilitySlots.length} ${t('details.slots')}`}
                            variant="outlined"
                            color="info"
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={<ScheduleIcon />}
                            label={t('details.noSlots')}
                            variant="outlined"
                            color="warning"
                          />
                        )}
                        {/* Stock / variantes : indicateur en temps réel basé sur les données produit */}
                        {(() => {
                          const allOptions = Array.isArray(product.variants)
                            ? product.variants.flatMap(v => Array.isArray(v?.options) ? v.options : [])
                            : [];
                          if (allOptions.length > 0) {
                            const outList = allOptions.filter(o => (parseInt(o?.quantity, 10) || 0) === 0);
                            const lowList = allOptions.filter(o => { const q = parseInt(o?.quantity, 10) || 0; return q > 0 && q <= 3; });
                            if (outList.length > 0) {
                              return (
                                <Tooltip title={outList.map(o => `${o.value} : épuisé`).join(' · ')}>
                                  <Chip
                                    size="small"
                                    icon={<InventoryIcon />}
                                    label={`⚠ ${outList.length} épuisé${outList.length > 1 ? 's' : ''}`}
                                    color="error"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              );
                            }
                            if (lowList.length > 0) {
                              return (
                                <Tooltip title={lowList.map(o => `${o.value} : ${parseInt(o.quantity, 10) || 0} restant(s)`).join(' · ')}>
                                  <Chip
                                    size="small"
                                    icon={<InventoryIcon />}
                                    label="⚠ Stock bas"
                                    color="warning"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              );
                            }
                            return (
                              <Chip
                                size="small"
                                icon={<InventoryIcon />}
                                label={`${t('details.quantity')}: ${product.quantity}`}
                                variant="outlined"
                                color="success"
                              />
                            );
                          }
                          return (
                            <Chip
                              size="small"
                              label={`${t('details.quantity')}: ${product.quantity}`}
                              variant="outlined"
                            />
                          );
                        })()}
                      </Box>

                      {/* Dates */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          mt: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('common:listing.card.created')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem' }}>
                            {formatDate(product.createdAt)}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            {getTimeAgo(product.createdAt)}
                          </Typography>
                        </Box>
                        {product.updatedAt && product.updatedAt !== product.createdAt && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('common:listing.card.updated')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem' }}>
                              {formatDate(product.updatedAt)}
                            </Typography>
                            <Typography variant="caption" color="secondary">
                              {getTimeAgo(product.updatedAt)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    {/* Actions */}
                    <CardActions
                      sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!userIsBanned && (
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={(e) => { e.stopPropagation(); navigate(`/edit-product/${product.id}`); }}
                          sx={{
                            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                            color: 'white',
                            '&:hover': { opacity: 0.9 }
                          }}
                        >
                          {t('actions.edit')}
                        </Button>
                      )}
                      <ProductCardMenu
                        product={product}
                        onView={() => navigate(`/product/${product.id}`)}
                        onEdit={() => navigate(`/edit-product/${product.id}`)}
                        onToggleStatus={(s) => handleToggleStatus(product, s)}
                        onDelete={() => handleDelete(product.id)}
                        userIsBanned={userIsBanned}
                        t={t}
                      />
                    </CardActions>

                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* ── Dialog suppression individuelle ───── */}
      <Dialog open={deleteDialogOpen} onClose={() => !submitting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('deleteConfirm.message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>{t('form.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog pause individuelle ───── */}
      <Dialog open={pauseDialogOpen} onClose={() => !submitting && setPauseDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('myProducts.bulk.pauseTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('myProducts.bulk.pauseInfo')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)} disabled={submitting}>{t('form.cancel')}</Button>
          <Button onClick={handleConfirmPause} color="warning" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : t('actions.pause')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog bulk pause ───── */}
      <Dialog open={bulkPauseDialogOpen} onClose={() => !bulkSubmitting && setBulkPauseDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('myProducts.bulk.pauseTitle')}</DialogTitle>
        {bulkSubmitting && <LinearProgress />}
        <DialogContent>
          <DialogContentText>
            {t('myProducts.bulk.pauseConfirm', {
              count: Array.from(selectedIds).filter(id => products.find(p => p.id === id)?.status === 'active').length
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPauseDialogOpen(false)} disabled={bulkSubmitting}>{t('form.cancel')}</Button>
          <Button onClick={handleBulkPause} color="warning" variant="contained" disabled={bulkSubmitting}>
            {bulkSubmitting ? <CircularProgress size={18} /> : t('myProducts.bulk.pause')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog bulk delete ───── */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => !bulkSubmitting && setBulkDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('myProducts.bulk.deleteTitle')}</DialogTitle>
        {bulkSubmitting && <LinearProgress />}
        <DialogContent>
          {(() => {
            const lockedCount = Array.from(selectedIds).filter(id => products.find(p => p.id === id)?.has_reservations).length;
            const deletableCount = selectedIds.size - lockedCount;
            return (
              <>
                <DialogContentText>
                  {t('myProducts.bulk.deleteWarning', { count: deletableCount })}
                </DialogContentText>
                {lockedCount > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    {t('myProducts.bulk.deleteReservationWarning', { count: lockedCount })}
                  </Alert>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkSubmitting}>{t('form.cancel')}</Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={bulkSubmitting || Array.from(selectedIds).every(id => products.find(p => p.id === id)?.has_reservations)}
          >
            {bulkSubmitting ? <CircularProgress size={18} /> : t('myProducts.bulk.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de choix création */}
      <CreateChoiceModal 
        open={createChoiceModalOpen} 
        onClose={() => setCreateChoiceModalOpen(false)} 
      />

      {/* Sidebar de navigation Annonces / Produits */}
      <ListingProductSidebar activePage="products" user={user} />
    </Box>
  );
}
